import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import Stack from '@mui/material/Stack';

import { VpButton } from '@vp-ui/components';
import { VpHookFormInput, VpHookFormSelect } from '@vp-ui/inputs';

import {
  StyledStack,
  StyledButtonStack,
  FullWidthStack,
  SubTitle,
  Title,
} from '@vp-ui/dialogs/CreateCompilationDialog/styles';
import { getUser } from '@src/redux/modules/auth';
import { CompilationSvg } from '@vp-ui/svgs';
import { Box, FormControlLabel, FormHelperText, Radio, RadioGroup } from '@mui/material';
import { useFetchAllCampaigns } from '@src/hooks';
import { useFetchCampaignByLink } from '@src/hooks/react-query/useCampaignData';
import VpFieldMappingInputs from '@vp-ui/form-fields/VpFieldMappingInputs/VpFieldMappingInputs';

const CreateCompilationForm = ({ onCancel = () => {}, loading = false, username = '' }) => {
  const [libraryType, setLibraryType] = React.useState('manual');

  const handleChange = (event) => {
    setLibraryType(event.target.value);
  };

  const {
    formState: { isValid },
    register,
    setValue
  } = useFormContext();

  const selectedCampaignId = useWatch({ name: 'campaignId' });
  const titleElementId = useWatch({ name: 'titleElementId' });
  const descriptionElementId = useWatch({ name: 'descriptionElementId' });

  const { data } = useFetchAllCampaigns();

  const campaigns = useMemo(
    () => data?.map((m) => ({ id: m?.value, label: m?.label })) || [],
    [data]
  );

  const selectedCampaignLink = useMemo(() => {
    if (!selectedCampaignId || !data) return null;
    return data.find((m) => m.value === String(selectedCampaignId))?.campaignLink || null;
  }, [selectedCampaignId, data]);

  const { data: campaignDetails } = useFetchCampaignByLink(
    libraryType === 'automated' ? selectedCampaignLink : null
  );
  const campaignElements = campaignDetails?.campaignElements || [];

  useEffect(() => {
    setValue('titleElementId', null);
    setValue('descriptionElementId', null);
  }, [selectedCampaignId]);

  const handleFieldMappingChange = ({ titleElementId, descriptionElementId }) => {
    setValue('titleElementId', titleElementId);
    setValue('descriptionElementId', descriptionElementId);
  };

  return (
    <StyledStack>
      <FullWidthStack>
        <CompilationSvg width="150px" height="auto" />
        <Title variant="h3">Get Started</Title>
        <SubTitle variant="dialogBody2">
          {`Create an SEO friendly page to showcase your videos.`}
        </SubTitle>
      </FullWidthStack>

      <Stack gap="25px">
        <VpHookFormInput
          name="title"
          label="Video Library Name"
          placeholder="Video Library Name"
          defaultValue={`${username ? `${username}'s` : 'My'} Video Library`}
          disableHelperText
          smallLabel
          required
        />
        <input {...register('libraryType')} type="hidden" defaultValue="manual" />
        <RadioGroup
          defaultValue="manual"
          name="radio-buttons-group"
          value={libraryType}
          onChange={handleChange}
        >
          <FormControlLabel value="manual" control={<Radio />} label="Manual" />
          <FormHelperText sx={{pl: 9, mb: 2}}>
            Add videos to this video library one by one.
          </FormHelperText>
          <FormControlLabel value="automated" control={<Radio />} label="Automated" />
          <FormHelperText sx={{pl: 9}}>
            Existing and future videos that belongs to the campaign you set will automatically be
            added to this video library.
          </FormHelperText>
        </RadioGroup>
        <Box
          sx={{
            minWidth: { md: '200px' },
            display: libraryType === 'automated' ? 'block' : 'none',
          }}
        >
          <VpHookFormSelect
            name="campaignId"
            label="Campaign"
            disabled={libraryType === 'manual'}
            placeholder="Campaigns"
            options={campaigns || []}
            smallLabel
            data-testid="campaignId"
            disableHelperText
            {...register('campaignId')}
          />
        </Box>
        {libraryType === 'automated' && selectedCampaignId && (
          <Box>
            <input {...register('titleElementId')} type="hidden" />
            <input {...register('descriptionElementId')} type="hidden" />
            <FormHelperText sx={{ mb: 4 }}>
              Select what field you want to set for your title and description for each video.
            </FormHelperText>
            <VpFieldMappingInputs
              elements={campaignElements}
              titleElementId={titleElementId || null}
              descriptionElementId={descriptionElementId || null}
              onChange={handleFieldMappingChange}
            />
          </Box>
        )}
      </Stack>
      <StyledButtonStack>
        <VpButton
          variant="contained"
          color="neutral"
          loading={loading}
          disabled={loading}
          onClick={onCancel}
        >
          Cancel
        </VpButton>
        <VpButton
          variant="contained"
          name="createCompilationButton"
          type="submit"
          loading={loading}
          disabled={!isValid || loading}
        >
          Create Video Library
        </VpButton>
      </StyledButtonStack>
    </StyledStack>
  );
};
CreateCompilationForm.propTypes = {
  username: PropTypes.string,
  loading: PropTypes.bool,
  onCancel: PropTypes.func,
};

const mapStateToProps = (state) => ({
  username: getUser(state)?.firstName ?? '',
});

export default connect(mapStateToProps)(CreateCompilationForm);



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/CreateCompilationDialog/CreateCompilationForm.jsx